import "./App.css";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { Provider, useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import store from "./store/store";
import Header from "./components/Header";
import Slider from "./components/Slider";
import DepositWithdrawModal from "./components/Modals/DepositWithdrawModal";
import ContactModal from "./components/Modals/ContactModal";
import LoginModal from "./components/Modals/LoginModal";
import Footer from "./components/Footer";
import PageContent from "./components/PageContent";
import { useEffect, useState } from "react";
import {
  getWebsiteSetting,
  getUsersByID,
  getPlayerBalance,
  getPlayerCash,
  getAllRanking,
} from "./store/slices/user";
import useAuth from "./hooks/useAuth";
import { Icon } from "@iconify/react";
import SocketProvider from "./SocketContext";
import socketManager from "./utils/socket";

function AppLayout() {
  const location = useLocation();
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  // Socket notification states
  const [open2, setOpen2] = useState(false);
  const [openSuccessDeposit, setOpenSuccessDeposit] = useState(false);
  const [lastPong, setLastPong] = useState(null);
  const [lastPong2, setLastPong2] = useState(null);
  const dispatch = useDispatch();
  const singleUsers = useSelector((state) => state.user.singleUsers);
  const websiteSetting = useSelector((state) => state.user.websiteSetting);
  const { user, isAuthenticated } = useAuth();
  const userId = user?.id;
  const currentUser = Array.isArray(singleUsers)
    ? singleUsers[0]
    : singleUsers?.data?.[0];
  const withdrawConditionDetail = currentUser?.withdrawConditionDetail;
  const promotionDetail = currentUser?.PromotionDetail;
  const promotionStatus = currentUser?.PromotionStatus;
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/$/,
    "",
  );
  const backgroundPath = Array.isArray(websiteSetting)
    ? websiteSetting[0]?.webBackgroundUrl
    : websiteSetting?.data?.[0]?.webBackgroundUrl ||
      websiteSetting?.webBackgroundUrl;
  const backgroundUrl = backgroundPath
    ? backgroundPath.startsWith("http")
      ? backgroundPath
      : `${apiBaseUrl}${
          backgroundPath.startsWith("/") ? "" : "/"
        }${backgroundPath}`
    : "";
  const isMenuPage = location.pathname === "/menu";
  const isHomePage = location.pathname === "/home" || location.pathname === "/";

  // Turn / promotion status bars (Gold Dark)
  const renderTurnPromo = () => {
    if (!withdrawConditionDetail && !promotionDetail) return null;
    const isPromoActive = promotionStatus === "ACTIVE";
    return (
      <div className="lux-container lux-status">
        {withdrawConditionDetail && (
          <div className="lux-status__bar lux-status__bar--warn">
            <span className="lux-status__icon">
              <Icon icon="solar:shield-warning-bold" width={22} />
            </span>
            <div className="lux-status__body">
              <div className="lux-status__label">ติดเงื่อนไขถอน</div>
              <div className="lux-status__title">กรุณาทำเทิร์นให้ครบก่อนถอนเงิน</div>
              <div className="lux-status__text">{withdrawConditionDetail}</div>
            </div>
          </div>
        )}
        {promotionDetail && (
          <div
            className={`lux-status__bar ${
              isPromoActive ? "lux-status__bar--gold" : "lux-status__bar--idle"
            }`}
          >
            <span className="lux-status__icon">
              <Icon
                icon={isPromoActive ? "solar:gift-bold" : "solar:gift-line-duotone"}
                width={22}
              />
            </span>
            <div className="lux-status__body">
              <div className="lux-status__label">
                {isPromoActive ? "กำลังใช้โปรโมชั่น" : "เลือกโปรแล้ว"}
              </div>
              <div className="lux-status__title">{promotionDetail}</div>
              {!isPromoActive && (
                <div className="lux-status__text">ฝากเงินเพื่อเปิดใช้งานโปรโมชั่นนี้</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    dispatch(getWebsiteSetting());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getAllRanking());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    const body = document.body;
    if (backgroundUrl) {
      body.style.setProperty("--app-bg-image", `url("${backgroundUrl}")`);
    } else {
      // fall back to the Gold Dark default defined in index.css
      body.style.removeProperty("--app-bg-image");
    }
  }, [backgroundUrl]);

  // fetch latest singleUsers by current user id
  useEffect(() => {
    if (userId) {
      dispatch(getUsersByID(userId));
    }
  }, [userId, dispatch]);

  // Replace previous socket effect with instance-based binding
  useEffect(() => {
    const socket = socketManager;

    if (!socket || !user || !user.id) return;

    const baseSocket = socket.connect();
    const userChannel = String(user.id);
    const depositName = `deposit-${user.id}`;
    const depositPaymentName = `depositpayment-${user.id}`;

    try {
      baseSocket.emit("join-user-room", user.id);
    } catch {}

    const onNotApprove = (res) => {
      setLastPong(`ไม่อนุมัติถอนเงิน! ${res}`);
      setOpen2(true);
      setTimeout(() => {
        dispatch(getPlayerBalance(user));
        setOpen2(false);
      }, 6000);
    };

    const onDeposit = (res) => {
      setLastPong2(`ฝากเงินจำนวน ${res} บาท สำเร็จ!`);
      setOpenSuccessDeposit(true);
      setTimeout(() => {
        dispatch(getPlayerBalance(user));
        dispatch(getUsersByID(user));
        dispatch(getPlayerCash(user));
        setOpenSuccessDeposit(false);
      }, 6000);
    };

    const onDepositPayment = (res) => {
      console.log("Received event:", depositPaymentName, "with data:", res);
      setLastPong2(`ฝากเงินจำนวน ${res} บาท สำเร็จ!`);
      setOpenSuccessDeposit(true);
      setTimeout(() => {
        dispatch(getPlayerBalance(user));
        dispatch(getUsersByID(user));
        dispatch(getPlayerCash(user));
        setOpenSuccessDeposit(false);

        const depositModal = document.querySelector(".deposit-modal");
        if (depositModal) {
          const depositComponent =
            depositModal.__reactFiber$?.return?.stateNode;
          if (depositComponent?.handleClose) {
            depositComponent.handleClose();
          }
        }
      }, 6000);
    };

    baseSocket.on(userChannel, onNotApprove);
    baseSocket.on(depositName, onDeposit);
    baseSocket.on(depositPaymentName, onDepositPayment);

    return () => {
      try {
        baseSocket.off(userChannel, onNotApprove);
        baseSocket.off(depositName, onDeposit);
        baseSocket.off(depositPaymentName, onDepositPayment);
      } catch (e) {
        // ignore
      }
    };
  }, [dispatch, user]);

  // Listen for global user updates (fallback)
  useEffect(() => {
    const onUserUpdate = (e) => {
      if (e?.detail?.userId && e.detail.userId === userId) {
        dispatch(getUsersByID(userId));
      }
    };
    window.addEventListener("app:userUpdate", onUserUpdate);
    return () => window.removeEventListener("app:userUpdate", onUserUpdate);
  }, [dispatch, userId]);

  const isLoginPage =
    location.pathname === "/login" || location.pathname === "/register";

  if (isLoginPage) {
    return (
      <div className="App">
        <PageContent />
      </div>
    );
  }

  return (
    <div className="App lux-app">
      <div className="lux-sticky">
        <Header
          onLoginClick={() => setIsLoginModalOpen(true)}
          onContactClick={() => setIsContactModalOpen(true)}
        />
      </div>

      {/* Socket alerts */}
      {open2 && (
        <div className="app-alert-wrapper" style={{ top: 96 }}>
          <div className="app-alert app-alert--warning">
            <Icon icon="solar:danger-triangle-bold" width={22} height={22} />
            <span>{lastPong}</span>
            <button
              className="app-alert__close"
              aria-label="close"
              onClick={() => setOpen2(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      {openSuccessDeposit && (
        <div className="app-alert-wrapper" style={{ top: 140 }}>
          <div className="app-alert app-alert--success">
            <Icon icon="solar:check-circle-bold" width={22} height={22} />
            <span>{lastPong2}</span>
            <button
              className="app-alert__close"
              aria-label="close"
              onClick={() => setOpenSuccessDeposit(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <main className={`lux-main${isMenuPage ? " lux-main--menu" : ""}`}>
        {/* Home renders its own hero (with slider); other pages keep the banner on top */}
        {!isMenuPage && !isHomePage && (
          <div className="lux-container lux-page-banner">
            <Slider />
          </div>
        )}
        {!isMenuPage && renderTurnPromo()}
        <PageContent />
      </main>

      <Footer
        onDepositClick={() => setIsDepositModalOpen(true)}
        onContactClick={() => setIsContactModalOpen(true)}
      />

      {/* Modals */}
      <DepositWithdrawModal
        isOpen={isDepositModalOpen}
        onClose={() => setIsDepositModalOpen(false)}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </div>
  );
}

function App() {
  return (
    <Provider store={store}>
      <SocketProvider>
        <Router>
          <AppLayout />
        </Router>
      </SocketProvider>
    </Provider>
  );
}

export default App;
